import { SignPostLeftStroke, XmarkStroke } from '@lineiconshq/free-icons';
import { type ChangeEvent, useRef, useState } from 'react';
import { Lineicons } from '@lineiconshq/react-lineicons';
import { Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ExcelFileTabProps } from './types';
import { cn } from '@/lib/utils';

const ExcelFileTab = ({ onFileChange, label = 'Tệp đính kèm', className = 'w-[500px]' }: ExcelFileTabProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setFileName(file?.name ?? '');
    onFileChange?.(file);
  };

  const handleClearFile = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    setFileName('');
    onFileChange?.(null);
  };

  return (
    <div className='p-4'>
      <div className='flex items-start gap-4'>
        <Label htmlFor='excel-import-file' className='mt-3 w-[115px] shrink-0 text-[13px] font-medium text-slate-700'>
          {label}
        </Label>
        <input
          ref={inputRef}
          id='excel-import-file'
          type='file'
          accept='.xlsx,.xls,.csv'
          onChange={handleFileChange}
          className='sr-only'
        />
        <div
          className={cn(
            'flex min-h-12 flex-1 items-center gap-3 border border-dashed border-slate-300 bg-slate-50/80 px-3 py-2 transition-colors hover:border-main hover:bg-main/5',
            className
          )}
        >
          <div className='flex size-8 shrink-0 items-center justify-center rounded-sm bg-white text-emerald-700 shadow-sm ring-1 ring-slate-200'>
            <Lineicons aria-hidden='true' icon={SignPostLeftStroke} />
          </div>

          <div className='min-w-0 flex-1'>
            <div className='truncate text-[13px] font-medium text-slate-800'>{fileName || 'Chưa chọn file'}</div>
            <div className='text-[11px] text-slate-500'>.xlsx, .xls, .csv</div>
          </div>

          {fileName && (
            <Button
              type='button'
              variant='ghost'
              size='sm'
              onClick={handleClearFile}
              className='h-8 w-8 shrink-0 p-0 text-slate-500 hover:bg-white hover:text-red-600'
              aria-label='Xóa file đã chọn'
            >
              <Lineicons aria-hidden='true' icon={XmarkStroke} />
            </Button>
          )}

          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={() => inputRef.current?.click()}
            className='h-8 shrink-0 gap-2 rounded-sm border-main px-3 text-[12px] font-semibold text-main hover:bg-main/10'
          >
            <Upload size={14} />
            Chọn file
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExcelFileTab;
